import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30_000; // matches backend cache TTL

/**
 * useSystemHealth — polls /api/system/health every 30s.
 *
 * Returns:
 *   - data:         the latest health payload (null until first fetch)
 *   - loading:      true on first fetch
 *   - error:        last fetch error message, if any
 *   - refresh():    force-bypass server cache + refetch
 *   - lastFetchAt:  timestamp of the last successful response
 */
export function useSystemHealth({ enabled = true, intervalMs = POLL_MS } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchAt, setLastFetchAt] = useState(0);
  const inflight = useRef(false);

  const fetchOnce = useCallback(async (force = false) => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const url = `${BACKEND}/api/system/health${force ? "?force=true" : ""}`;
      const r = await axios.get(url, { timeout: 8000 });
      setData(r.data);
      setError(null);
      setLastFetchAt(Date.now());
    } catch (e) {
      setError(e?.message || "Health probe failed");
    } finally {
      setLoading(false);
      inflight.current = false;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    Promise.resolve().then(() => fetchOnce(false));
    const id = setInterval(() => fetchOnce(false), intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs, fetchOnce]);

  const refresh = useCallback(() => fetchOnce(true), [fetchOnce]);

  return { data, loading, error, refresh, lastFetchAt };
}

export default useSystemHealth;
